import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import api from '../hooks/api';

const STATUSES = ['available', 'assigned', 'in_repair', 'retired', 'lost'];

const emptyForm = {
  asset_tag: '', name: '', brand: '', model: '', serial_no: '',
  ip_address: '', invoice_number: '', vendor_name: '',
  status: 'available', branch_id: '', department_id: '', employee_id: '',
  parent_id: '',
  purchase_date: '', purchase_cost: '', warranty_until: '', notes: '',
  location_label: '', cctv_channel: '',
  cctv_monitored_ip: '', cctv_monitored_hostname: '',
};

function StatusBadge({ status }) {
  return <span className={`badge badge-status badge-${status}`}>{status.replace('_', ' ')}</span>;
}

export default function CCTVPage() {
  const { user } = useAuth();
  const [allDevices, setAllDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterNvr, setFilterNvr] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  const loadDevices = useCallback(async () => {
    setAllDevices(await api.get('/api/devices?include_sub=true'));
  }, []);

  const loadMeta = useCallback(async () => {
    const [dt, b, d, e] = await Promise.all([
      api.get('/api/device-types'),
      api.get('/api/branches'),
      api.get('/api/departments'),
      api.get('/api/employees'),
    ]);
    setDeviceTypes(dt); setBranches(b); setDepartments(d); setEmployees(e);
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const cctvTypeId = deviceTypes.find(t => t.name === 'CCTV Camera')?.id || '';
  const nvrTypeId = deviceTypes.find(t => t.name === 'NVR')?.id || '';

  const cameras = allDevices.filter(d => d.type_id === cctvTypeId);
  const nvrs = allDevices.filter(d => d.type_id === nvrTypeId);
  const tagById = Object.fromEntries(allDevices.map(d => [d.id, d.asset_tag]));
  const nameById = Object.fromEntries(allDevices.map(d => [d.id, d.name]));

  const filteredDepts = departments.filter(d => !form.branch_id || d.branch_id === form.branch_id);
  const filteredEmps = employees.filter(e =>
    (!form.branch_id || e.branch_id === form.branch_id) &&
    (!form.department_id || e.department_id === form.department_id)
  );
  const filteredNvrsForForm = nvrs.filter(n => !form.branch_id || n.branch_id === form.branch_id);
  const filterDeptOptions = departments.filter(d => !filterBranch || d.branch_id === filterBranch);

  async function openAdd() {
    setError('');
    if (!cctvTypeId) { alert('"CCTV Camera" device type not found. Run migrations / restart backend.'); return; }
    try {
      const { asset_tag } = await api.get('/api/devices/next-tag');
      setForm({ ...emptyForm, asset_tag });
      setModal('add');
    } catch (err) { alert(err.message); }
  }

  function openEdit(d) {
    setForm({
      asset_tag: d.asset_tag, name: d.name, brand: d.brand || '',
      model: d.model || '', serial_no: d.serial_no || '',
      ip_address: d.ip_address || '', invoice_number: d.invoice_number || '', vendor_name: d.vendor_name || '',
      status: d.status, branch_id: d.branch_id, department_id: d.department_id || '',
      employee_id: d.employee_id || '', parent_id: d.parent_id || '',
      purchase_date: d.purchase_date || '',
      purchase_cost: d.purchase_cost ?? '', warranty_until: d.warranty_until || '',
      notes: d.notes || '',
      location_label: d.location_label || '', cctv_channel: d.cctv_channel || '',
      cctv_monitored_ip: d.cctv_monitored_ip || '', cctv_monitored_hostname: d.cctv_monitored_hostname || '',
    });
    setError(''); setModal({ mode: 'edit', id: d.id });
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault(); setError('');
    try {
      const payload = {
        ...form,
        type_id: cctvTypeId,
        purchase_cost: form.purchase_cost ? parseFloat(form.purchase_cost) : null,
        employee_id: form.employee_id || null,
        department_id: form.department_id || null,
        parent_id: form.parent_id || null,
      };
      if (modal === 'add') await api.post('/api/devices', payload);
      else await api.put('/api/devices/' + modal.id, payload);
      setModal(null); loadDevices();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(d) {
    if (!window.confirm(`Delete camera ${d.asset_tag} - ${d.name}?`)) return;
    await api.delete('/api/devices/' + d.id);
    loadDevices();
  }

  const visible = cameras.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      const ok = d.asset_tag.toLowerCase().includes(q) || d.name.toLowerCase().includes(q) ||
        (d.location_label || '').toLowerCase().includes(q) ||
        (d.cctv_monitored_hostname || '').toLowerCase().includes(q) ||
        (d.cctv_monitored_ip || '').toLowerCase().includes(q) ||
        (d.ip_address || '').toLowerCase().includes(q);
      if (!ok) return false;
    }
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterBranch && d.branch_id !== filterBranch) return false;
    if (filterDept && d.department_id !== filterDept) return false;
    if (filterNvr === '__none__' && d.parent_id) return false;
    if (filterNvr && filterNvr !== '__none__' && d.parent_id !== filterNvr) return false;
    if (filterLocation && !(d.location_label || '').toLowerCase().includes(filterLocation.toLowerCase())) return false;
    return true;
  });

  const activeFilterCount =
    (filterStatus ? 1 : 0) + (filterBranch ? 1 : 0) + (filterDept ? 1 : 0) +
    (filterNvr ? 1 : 0) + (filterLocation ? 1 : 0);

  function clearFilters() {
    setSearch(''); setFilterStatus(''); setFilterBranch('');
    setFilterDept(''); setFilterNvr(''); setFilterLocation('');
  }

  function handleExport() {
    const columns = [
      ['Asset Tag', d => d.asset_tag],
      ['Name', d => d.name],
      ['Brand', d => d.brand],
      ['Model', d => d.model],
      ['Serial No', d => d.serial_no],
      ['IP Address', d => d.ip_address],
      ['Location', d => d.location_label],
      ['NVR Tag', d => d.parent_id ? (tagById[d.parent_id] || '') : ''],
      ['NVR Name', d => d.parent_id ? (nameById[d.parent_id] || '') : ''],
      ['Channel', d => d.cctv_channel],
      ['Monitored Computer IP', d => d.cctv_monitored_ip],
      ['Monitored Computer Hostname', d => d.cctv_monitored_hostname],
      ['Status', d => d.status],
      ['Branch', d => d.branch_name],
      ['Department', d => d.department_name],
      ['Assigned To', d => d.employee_name],
      ['Emp Code', d => d.emp_code],
      ['Invoice Number', d => d.invoice_number],
      ['Vendor Name', d => d.vendor_name],
      ['Purchase Date', d => d.purchase_date],
      ['Purchase Cost', d => d.purchase_cost],
      ['Warranty Until', d => d.warranty_until],
      ['Notes', d => d.notes],
      ['Created At', d => d.created_at ? new Date(d.created_at).toISOString() : ''],
    ];
    const esc = v => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map(c => esc(c[0])).join(',');
    const body = visible.map(r => columns.map(c => esc(c[1](r))).join(',')).join('\n');
    const csv = '﻿' + header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `cctv-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>CCTV Cameras</h1>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={handleExport}>Export CSV</button>
          {(user?.role === 'admin' || user?.role === 'manager') &&
            <button className="btn btn-primary" onClick={openAdd}>+ Add Camera</button>}
        </div>
      </div>
      <div className="filters">
        <input placeholder="Search asset tag, name, location, monitored computer, IP…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {user?.role === 'admin' && (
          <select value={filterBranch} onChange={e => { setFilterBranch(e.target.value); setFilterDept(''); }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {filterDeptOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterNvr} onChange={e => setFilterNvr(e.target.value)}>
          <option value="">All NVRs</option>
          <option value="__none__">No NVR linked</option>
          {nvrs.map(n => <option key={n.id} value={n.id}>{n.asset_tag} · {n.name}</option>)}
        </select>
        <input placeholder="Location contains…" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} style={{ minWidth: 160 }} />
        {(search || activeFilterCount > 0) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear</button>
        )}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Asset Tag</th><th>Name</th><th>Location</th><th>Branch / Dept</th>
              <th>NVR · Channel</th><th>Monitored Computer</th><th>IP</th>
              <th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(d => (
              <tr key={d.id}>
                <td className="mono">{d.asset_tag}</td>
                <td>
                  {d.name}
                  {d.brand ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {d.brand}</span> : ''}
                </td>
                <td>{d.location_label || '—'}</td>
                <td>{d.branch_name}{d.department_name ? ` / ${d.department_name}` : ''}</td>
                <td>
                  {d.parent_id ? (
                    <>
                      <span className="mono">{tagById[d.parent_id] || '?'}</span>
                      {d.cctv_channel ? <span style={{ color: 'var(--text-muted)' }}> · ch {d.cctv_channel}</span> : ''}
                    </>
                  ) : '—'}
                </td>
                <td>
                  {d.cctv_monitored_hostname || d.cctv_monitored_ip ? (
                    <>
                      {d.cctv_monitored_hostname && <div>{d.cctv_monitored_hostname}</div>}
                      {d.cctv_monitored_ip && <div className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.cctv_monitored_ip}</div>}
                    </>
                  ) : '—'}
                </td>
                <td className="mono">{d.ip_address || '—'}</td>
                <td><StatusBadge status={d.status} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(user?.role === 'admin' || user?.role === 'manager') &&
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(d)}>Edit</button>}
                  {user?.role === 'admin' &&
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDelete(d)}>Delete</button>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={9} className="empty">No cameras found</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Camera' : 'Edit Camera'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label>Asset Tag {modal === 'add' ? '(auto-generated)' : ''}</label>
                <input value={form.asset_tag} disabled style={{ fontFamily: 'var(--mono)', background: 'var(--surface2)' }} />
              </div>
              <div className="form-group">
                <label>Camera Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Brand</label>
                <input value={form.brand} onChange={e => set('brand', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Model</label>
                <input value={form.model} onChange={e => set('model', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Serial No.</label>
                <input value={form.serial_no} onChange={e => set('serial_no', e.target.value)} />
              </div>
              <div className="form-group">
                <label>IP Address</label>
                <input value={form.ip_address} onChange={e => set('ip_address', e.target.value)} placeholder="192.168.1.1" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name</label>
                <input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Invoice Number</label>
                <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} required>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Branch *</label>
              <select value={form.branch_id} onChange={e => { set('branch_id', e.target.value); set('department_id', ''); set('employee_id', ''); set('parent_id', ''); }} required>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Department *</label>
              <select value={form.department_id} onChange={e => { set('department_id', e.target.value); set('employee_id', ''); }} required disabled={!form.branch_id}>
                <option value="">{form.branch_id ? 'Select department…' : 'Select branch first'}</option>
                {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned Employee</label>
              <select value={form.employee_id} onChange={e => set('employee_id', e.target.value)} disabled={!form.department_id}>
                <option value="">{form.department_id ? 'Unassigned' : 'Select department first'}</option>
                {filteredEmps.map(e => <option key={e.id} value={e.id}>{e.name}{e.emp_code ? ` (${e.emp_code})` : ''}</option>)}
              </select>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', margin: '20px 0 16px', paddingTop: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 12 }}>
                CCTV Details
              </div>
              <div className="form-group">
                <label>Physical Location</label>
                <input value={form.location_label} onChange={e => set('location_label', e.target.value)} placeholder="e.g. Front Entrance, Cash Counter, Vault Door" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>NVR / DVR</label>
                  <select value={form.parent_id} onChange={e => set('parent_id', e.target.value)} disabled={!form.branch_id}>
                    <option value="">{form.branch_id ? 'Not linked' : 'Select branch first'}</option>
                    {filteredNvrsForForm.map(n => <option key={n.id} value={n.id}>{n.asset_tag} · {n.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Channel #</label>
                  <input value={form.cctv_channel} onChange={e => set('cctv_channel', e.target.value)} placeholder="e.g. 01" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Monitored Computer IP</label>
                  <input value={form.cctv_monitored_ip} onChange={e => set('cctv_monitored_ip', e.target.value)} placeholder="192.168.1.45" />
                </div>
                <div className="form-group">
                  <label>Monitored Computer Hostname</label>
                  <input value={form.cctv_monitored_hostname} onChange={e => set('cctv_monitored_hostname', e.target.value)} placeholder="billing-pc-01" />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Purchase Date</label>
                <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Purchase Cost (₹)</label>
                <input type="number" step="0.01" value={form.purchase_cost} onChange={e => set('purchase_cost', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Warranty Until</label>
              <input type="date" value={form.warranty_until} onChange={e => set('warranty_until', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{modal === 'add' ? 'Add Camera' : 'Save Changes'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
